import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/ProductCard';
import { getProducts, addToCart } from '@/lib/api';
import { toast } from 'sonner';
import { PageSEO } from '@/components/SEO';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCollection, setSelectedCollection] = useState(
    searchParams.get('collection') || 'all'
  );

  useEffect(() => {
    loadProducts();
  }, [selectedCollection]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = selectedCollection !== 'all' ? { collection: selectedCollection } : {};
      const res = await getProducts(params);
      setProducts(res.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product) => {
    try {
      await addToCart({ product_id: product.product_id, quantity: 1 });
      toast.success('Added to cart');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to add items to cart');
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  const handleCollectionChange = (value) => {
    setSelectedCollection(value);
    if (value !== 'all') {
      setSearchParams({ collection: value });
    } else {
      setSearchParams({});
    }
  };

  const collections = [
    { value: 'all', label: 'All Products' },
    { value: 'Satin', label: 'Satin' },
    { value: 'Chiffon', label: 'Chiffon' },
    { value: 'Watercolor', label: 'Watercolor' }
  ];

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm uppercase tracking-widest font-medium mb-4">Collections</h3>
        <RadioGroup value={selectedCollection} onValueChange={handleCollectionChange}>
          {collections.map((collection) => (
            <div key={collection.value} className="flex items-center space-x-2 mb-3">
              <RadioGroupItem value={collection.value} id={collection.value} />
              <Label htmlFor={collection.value} className="cursor-pointer">
                {collection.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="shop-page">
      {PageSEO.shop(selectedCollection)}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-6xl font-medium tracking-tight mb-4">Shop</h1>
          <p className="text-lg text-stone-600 font-light">
            {selectedCollection !== 'all'
              ? `${selectedCollection} Collection`
              : `${products.length} Products`}
          </p>
        </div>

        <div className="flex gap-12">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0 sticky top-32 self-start">
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h2 className="text-sm uppercase tracking-widest font-medium mb-6">Filters</h2>
              <FilterContent />
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Mobile Filter */}
            <div className="lg:hidden mb-6">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <Filter className="mr-2 h-4 w-4" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                    <SheetDescription>
                      Filter products by collection and price
                    </SheetDescription>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterContent />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="space-y-4">
                    <div className="aspect-[3/4] skeleton rounded-lg" />
                    <div className="h-4 skeleton rounded w-3/4" />
                    <div className="h-4 skeleton rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-lg text-stone-600 mb-4">No products found</p>
                <Button
                  variant="outline"
                  onClick={() => handleCollectionChange('all')}
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8">
                {products.map((product) => (
                  <ProductCard
                    key={product.product_id}
                    product={product}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
